import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserNotifications, updateNotification } from '../sessions/sessionNotification';
import { AppDispatch, RootState } from '../../store';
import {
  Notifiable,
  Notification,
  ProjectNotification,
  UserNotification,
} from '../../#interfaces/interfaces';
import {
  getProjectContents,
  manageProjectContentsSliceActions,
} from '../sessions/projectsManagement/sessionManageProject';
import * as REQUEST_REQUIREMENTS from '../../api/requestRequirements';
import { useNavigate } from 'react-router-dom';
import { Badge, Box, CircularProgress, IconButton, Popover, styled, Typography } from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { UserAvatar } from '../../components/userAvatar/UserAvatar';
import { getUserGenericConfig } from '../sessions/sessionConfig/sessionUserConfigurations';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

const SmallBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    minWidth: 5,
    height: 12,
    borderRadius: '50%',
    background: 'linear-gradient(45deg, #8B0000, #FF1493)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    margin: '0px 2px 0px 0px',
  },
}));

export default function Notifications() {
  const currentUser = useSelector((state: RootState) => state.session.currentUser);
  const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
  const notificationsLength = useSelector(
    (state: RootState) => state.sessionUserConfig.userMoreInfo.notificationsUnvisualized
  );
  const notifications = useSelector(
    (state: RootState) => state.sessionNotifications.notifications
  );
  const loadingMore = useSelector(
    (state: RootState) => state.sessionNotifications.loadingMoreNotifications
  );
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [page, setPage] = useState<number>(1);
  const listRef = useRef<HTMLDivElement>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (authHeaders && currentUser) {
      dispatch(getUserNotifications({ authHeaders, urlParams: { page } }));
    }
  }, [currentUser, page]);

  useEffect(() => {
    if (!open) return;
    const container = listRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 20 && !loadingMore) {
        setPage((prev) => prev + 1);
      }
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loadingMore, open]);

  async function handleGetProjectContents(project: ProjectNotification) {
    setAnchorEl(null);
    const getParams = { authHeaders, userNickname: project.user.userMoreInfo.nickname, projectId: project.id };
    manageProjectContentsSliceActions.loadingPage();
    const response = await dispatch(getProjectContents(getParams));
    if (response) {
      const routeParams = { userNickname: project.user.userMoreInfo.nickname, projectId: project.id };
      const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({ ROUTE_PARAMS: routeParams });
      if (PRIVATE_ROUTES) navigate(PRIVATE_ROUTES.PROJECT_CONTENTS);
    }
  }

  async function handleNavigationUserProfile(userTarget: UserNotification) {
    const routeParams = { userNickname: userTarget.userMoreInfo.nickname };
    const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({ ROUTE_PARAMS: routeParams });
    return navigate(PRIVATE_ROUTES.USER);
  }

  async function handleUpdateNotification(notificationId: number) {
    if (authHeaders && currentUser?.nickname) {
      const response = await dispatch(
        updateNotification({
          authHeaders,
          urlParams: { userNickname: currentUser.nickname, notificationId },
          requestBody: { visualized: true },
        })
      );
      if (response.meta.requestStatus === 'fulfilled') {
        try {
          await dispatch(getUserGenericConfig({ authHeaders, userNickname: currentUser.nickname }));
        } catch {
          console.warn('Error updating user generic config after notification update');
        }
      }
    }
  }

  const hasUnread = notificationsLength && notificationsLength > 0;

  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
        sx={{
          color: open ? TEXT_PRIMARY : TEXT_SECONDARY,
          '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' },
          position: 'relative',
        }}
        aria-label="Notificações"
      >
        {hasUnread && (
          <Box sx={{
            position: 'absolute', top: 4, right: 4,
            bgcolor: BRAND_RED, color: '#fff',
            fontSize: 9, fontWeight: 700, lineHeight: 1,
            borderRadius: '4px', px: 0.4, py: 0.2, zIndex: 2,
            minWidth: 14, textAlign: 'center',
          }}>
            {notificationsLength > 10 ? '+10' : notificationsLength}
          </Box>
        )}
        {hasUnread ? <NotificationsIcon fontSize="small" /> : <NotificationsNoneIcon fontSize="small" />}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
        PaperProps={{
          ref: listRef,
          elevation: 0,
          sx: {
            width: { xs: 260, sm: 320 },
            minHeight: 80,
            maxHeight: { xs: 260, sm: 320 },
            overflowY: 'auto',
            bgcolor: BG_SECTION,
            border: BORDER_SUBTLE,
            borderRadius: '8px',
            mt: 0.5,
          },
        }}
      >
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {notifications.length === 0 && !loadingMore && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: 13 }}>
                No notifications yet
              </Typography>
            </Box>
          )}
          {notifications.map((item: Notification, index: number) => (
            <Box
              key={index}
              component="li"
              onClick={() => {
                if (!item.visualized) handleUpdateNotification(item.id);
                if (item.notifiableType === 'User' && item.actorUser.userMoreInfo) {
                  setAnchorEl(null);
                  return handleNavigationUserProfile(item.actorUser as UserNotification);
                } else if (item.notifiableType === 'Project' && item.actorUser.userMoreInfo) {
                  setAnchorEl(null);
                  return handleGetProjectContents(item.notifiable as ProjectNotification);
                }
              }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1, cursor: 'pointer',
                bgcolor: item.visualized ? 'transparent' : 'rgba(192,57,43,0.08)',
                borderBottom: BORDER_SUBTLE,
                '&:last-of-type': { borderBottom: 'none' },
                '&:hover': { bgcolor: BG_CARD },
                transition: 'background-color 0.15s',
              }}
            >
              <UserAvatar src={item.actorUser.userMoreInfo.avatarUrl} size={32} />
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: TEXT_PRIMARY, fontSize: { xs: 11, sm: 13 }, lineHeight: 1.4, userSelect: 'none', flex: 1 }}
                  dangerouslySetInnerHTML={{ __html: item.message }}
                />
                {!item.visualized && <SmallBadge badgeContent="" color="primary" />}
              </Box>
            </Box>
          ))}
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <CircularProgress size={20} sx={{ color: BRAND_RED }} />
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
}
